import {
  AttemptClaimRequest,
  AttemptClaimResponse,
  AttemptStartResponse,
  AttemptView,
  CampaignActionRequest,
  CampaignCreateRequest,
  CampaignReportResponse,
  CampaignReviewDecisionRequest,
  CampaignReviewQueueResponse,
  CampaignUpdateRequest,
  CampaignView,
  EarnCoinsResponse,
  HouseCampaignCreateRequest,
  MyCampaignsResponse,
  PartnerKeyCreateRequest,
  PartnerKeyCreateResponse,
  PartnerKeyView,
  PropertyOffersResponse,
  type CampaignCreateRequest as CampaignCreateBody,
  type CampaignReviewDecisionRequest as DecisionBody,
  type CampaignUpdateRequest as CampaignUpdateBody,
  type HouseCampaignCreateRequest as HouseCampaignBody,
} from "@townsy/contracts";
import { z } from "zod";
import { API_BASE, ApiError, apiFetch, isApiError } from "./client";

/**
 * Sponsored tasks for coins (M6, ADR-0011; docs/06 section 5). Every call goes to the same-origin API
 * with the session cookie; the server authorises, prices, reserves and awards. The browser never sends
 * an amount, a reward, a "completed" flag or a quiz verdict: a start has an empty body and a claim
 * carries at most the chosen quiz answer index.
 *
 * Paths are collected in {@link REWARD_PATHS} so the integrator can check them against the API in one
 * place. Where the route list and the repository comments disagree (earn list, link refresh, report
 * CSV) the first path is tried and the alternative is used only when the server answers that NO ROUTE
 * matches (never on a domain 404 such as "attempt not found").
 */

const enc = encodeURIComponent;

export const REWARD_PATHS = {
  propertyOffers: (propertyId: string) => `${API_BASE}/properties/${enc(propertyId)}/offers`,
  /** Brief: `/me/earn-coins`; repository comment: `/me/earn`. */
  earnCoins: [`${API_BASE}/me/earn-coins`, `${API_BASE}/me/earn`] as const,
  startAttempt: (offerId: string) => `${API_BASE}/offers/${enc(offerId)}/attempts`,
  attempt: (attemptId: string) => `${API_BASE}/offer-attempts/${enc(attemptId)}`,
  /** Brief: `POST …/refresh-link`; repository comment: `GET …/link`. */
  refreshLink: (attemptId: string) =>
    [
      { method: "POST" as const, path: `${API_BASE}/offer-attempts/${enc(attemptId)}/refresh-link` },
      { method: "GET" as const, path: `${API_BASE}/offer-attempts/${enc(attemptId)}/link` },
    ] as const,
  claim: (attemptId: string) => `${API_BASE}/offer-attempts/${enc(attemptId)}/claim`,
  cancel: (attemptId: string) => `${API_BASE}/offer-attempts/${enc(attemptId)}/cancel`,
  myCampaigns: `${API_BASE}/me/campaigns`,
  campaigns: `${API_BASE}/campaigns`,
  campaign: (campaignId: string) => `${API_BASE}/campaigns/${enc(campaignId)}`,
  campaignAction: (campaignId: string, action: CampaignAction) => `${API_BASE}/campaigns/${enc(campaignId)}/${action}`,
  campaignReport: (campaignId: string) => `${API_BASE}/campaigns/${enc(campaignId)}/report`,
  /** The API route is `/report/export` (verified against apps/api/src/modules/rewards/routes.ts). */
  campaignReportCsv: (campaignId: string) => [`${API_BASE}/campaigns/${enc(campaignId)}/report/export`] as const,
  partnerKeys: `${API_BASE}/partner-keys`,
  partnerKeyAction: (keyId: string, action: "rotate" | "revoke") => `${API_BASE}/partner-keys/${enc(keyId)}/${action}`,
  reviewCampaigns: `${API_BASE}/review/campaigns`,
  reviewCampaign: (campaignId: string) => `${API_BASE}/review/campaigns/${enc(campaignId)}`,
  reviewDecision: (campaignId: string) => `${API_BASE}/review/campaigns/${enc(campaignId)}/decision`,
  houseCampaigns: `${API_BASE}/admin/house-campaigns`,
  houseFund: (campaignId: string) => `${API_BASE}/admin/house-campaigns/${enc(campaignId)}/fund`,
};

export type CampaignAction = "submit" | "activate" | "pause";

/** The API's own "no route matches" answer (plugins/errors.ts), as opposed to a domain NOT_FOUND. */
export function isRouteMissing(err: unknown): boolean {
  return isApiError(err) && err.status === 404 && err.code === "NOT_FOUND" && /^No route matches/i.test(err.message);
}

/** Sponsored tasks are not running on this server: no routes yet, or SPONSORED_OFFERS_ENABLED is off. */
export function isRewardsUnavailable(err: unknown): boolean {
  return isRouteMissing(err) || (isApiError(err) && err.code === "FEATURE_DISABLED" && /sponsored tasks/i.test(err.message));
}

/** Try each candidate in order; move on only when the server says the route itself does not exist. */
export async function firstExistingRoute<T>(candidates: ReadonlyArray<() => Promise<T>>): Promise<T> {
  let last: unknown = new ApiError("NOT_FOUND", "No route matches this request");
  for (const run of candidates) {
    try {
      return await run();
    } catch (err) {
      if (!isRouteMissing(err)) throw err;
      last = err;
    }
  }
  throw last;
}

/** Accept `{ [key]: T }` or a bare `T` (the route list does not pin the wrapper). */
function wrappedOrBare<T extends z.ZodType>(schema: T, key: string) {
  return z.union([z.object({ [key]: schema }).loose().transform((o) => (o as Record<string, unknown>)[key] as z.infer<T>), schema]);
}

const AttemptLoose = wrappedOrBare(AttemptView, "attempt");
const CampaignLoose = wrappedOrBare(CampaignView, "campaign");
const PartnerKeyLoose = wrappedOrBare(PartnerKeyView, "key");
const PartnerKeyListLoose = z.union([z.object({ items: z.array(PartnerKeyView) }).loose().transform((o) => o.items), z.array(PartnerKeyView)]);
const HouseListLoose = z.union([z.object({ items: z.array(CampaignView) }).loose().transform((o) => o.items), z.array(CampaignView)]);

// ---------------------------------------------------------------- residents

/** The store's live sponsored tasks with this viewer's eligibility (guests: `sign_in_required`). */
export const fetchPropertyOffers = (propertyId: string, signal?: AbortSignal) =>
  apiFetch(REWARD_PATHS.propertyOffers(propertyId), { schema: PropertyOffersResponse, signal });

/** The "Earn coins" list across the town, the viewer's UTC-day allowance and their active attempt. */
export const fetchEarnCoins = (signal?: AbortSignal) =>
  firstExistingRoute(REWARD_PATHS.earnCoins.map((path) => () => apiFetch(path, { schema: EarnCoinsResponse, signal })));

/**
 * Start (or, for the same offer, re-open) a task attempt. The body is empty: the server checks the
 * account, caps and budget and reserves everything. `idempotencyKey` is generated once per start
 * intent and reused verbatim on a retry of the same request.
 */
export const postStartAttempt = (offerId: string, idempotencyKey: string) =>
  apiFetch(REWARD_PATHS.startAttempt(offerId), {
    method: "POST",
    body: {},
    headers: { "idempotency-key": idempotencyKey },
    schema: AttemptStartResponse,
  });

/** The owner's own attempt (never carries a link: use {@link postRefreshAttemptLink}). */
export const fetchAttempt = (attemptId: string, signal?: AbortSignal) => apiFetch(REWARD_PATHS.attempt(attemptId), { schema: AttemptLoose, signal });

/** A fresh same-origin `/out/<token>` link for an active website / partner attempt. */
export const postRefreshAttemptLink = (attemptId: string) =>
  firstExistingRoute(
    REWARD_PATHS.refreshLink(attemptId).map(
      (c) => () => apiFetch(c.path, c.method === "POST" ? { method: "POST", body: {}, schema: AttemptLoose } : { schema: AttemptLoose }),
    ),
  );

/** Claim the reward. Quiz tasks send the chosen answer index; nothing else is ever sent. */
export const postClaimAttempt = (attemptId: string, body: { answerIndex?: number }, idempotencyKey: string) =>
  apiFetch(REWARD_PATHS.claim(attemptId), {
    method: "POST",
    body: AttemptClaimRequest.parse(body),
    headers: { "idempotency-key": idempotencyKey },
    schema: AttemptClaimResponse,
  });

/** Give up an active attempt: the reserved budget and daily allowance are released once. */
export const postCancelAttempt = (attemptId: string) => apiFetch(REWARD_PATHS.cancel(attemptId), { method: "POST", body: {}, schema: AttemptLoose });

// ---------------------------------------------------------------- advertisers (campaign owners)

export const fetchMyCampaigns = () => apiFetch(REWARD_PATHS.myCampaigns, { schema: MyCampaignsResponse, cache: "no-cache" });

export const fetchCampaign = (campaignId: string) => apiFetch(REWARD_PATHS.campaign(campaignId), { schema: CampaignLoose, cache: "no-cache" });

export const postCampaign = (body: z.input<typeof CampaignCreateRequest>) =>
  apiFetch(REWARD_PATHS.campaigns, { method: "POST", body: CampaignCreateRequest.parse(body) satisfies CampaignCreateBody, schema: CampaignLoose });

export const patchCampaign = (campaignId: string, body: z.input<typeof CampaignUpdateRequest>) =>
  apiFetch(REWARD_PATHS.campaign(campaignId), { method: "PATCH", body: CampaignUpdateRequest.parse(body) satisfies CampaignUpdateBody, schema: CampaignLoose });

/** submit (for review) / activate / pause — optimistic concurrency on the campaign version. */
export const postCampaignAction = (campaignId: string, action: CampaignAction, expectedVersion: number) =>
  apiFetch(REWARD_PATHS.campaignAction(campaignId, action), {
    method: "POST",
    body: CampaignActionRequest.parse({ expectedVersion }),
    schema: CampaignLoose,
  });

export const fetchCampaignReport = (campaignId: string) => apiFetch(REWARD_PATHS.campaignReport(campaignId), { schema: CampaignReportResponse, cache: "no-cache" });

/** Filename from `Content-Disposition`, restricted to safe characters; otherwise a dated default. */
export function campaignCsvFilename(contentDisposition: string | null, campaignId: string, today: string): string {
  const m = contentDisposition ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition) : null;
  let raw = m?.[1] ?? "";
  try {
    raw = decodeURIComponent(raw);
  } catch {
    /* keep raw */
  }
  const safe = raw.replace(/[^A-Za-z0-9._-]/g, "");
  return safe && safe.toLowerCase().endsWith(".csv") ? safe : `townsy-campaign-${campaignId.slice(0, 8)}-${today}.csv`;
}

async function csvResponse(path: string): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: "include", headers: { accept: "text/csv" } });
  } catch {
    throw new ApiError("DEPENDENCY_UNAVAILABLE", "Can't reach Townsy right now. Check your connection.", { status: 0, retryable: true });
  }
  const type = res.headers.get("content-type") ?? "";
  if (res.ok && !type.includes("application/json")) return res;
  let message = res.ok ? "The server did not return a CSV file." : `The export failed (${res.status}).`;
  let code: ApiError["code"] = res.status === 401 ? "UNAUTHENTICATED" : res.status === 404 ? "NOT_FOUND" : res.status === 429 ? "RATE_LIMITED" : res.status >= 500 ? "INTERNAL" : "VALIDATION_FAILED";
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body.error?.message) message = body.error.message;
    if (body.error?.code) code = body.error.code as ApiError["code"];
  } catch {
    /* not JSON */
  }
  throw new ApiError(code, message, { status: res.ok ? 422 : res.status, retryable: res.status === 429 || res.status >= 500 });
}

/**
 * Download the campaign report as CSV (the server escapes spreadsheet formula prefixes). Fetched first
 * so a sign-in problem shows as a message instead of a JSON page.
 */
export async function downloadCampaignReportCsv(campaignId: string, today: string): Promise<void> {
  const res = await firstExistingRoute(REWARD_PATHS.campaignReportCsv(campaignId).map((p) => () => csvResponse(p)));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = campaignCsvFilename(res.headers.get("content-disposition"), campaignId, today);
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

// ---------------------------------------------------------------- partner keys (secret shown once)

export const fetchPartnerKeys = (advertiserId: string) =>
  apiFetch(`${REWARD_PATHS.partnerKeys}?advertiserId=${enc(advertiserId)}`, { schema: PartnerKeyListLoose, cache: "no-cache" });

export const postPartnerKey = (body: z.input<typeof PartnerKeyCreateRequest>) =>
  apiFetch(REWARD_PATHS.partnerKeys, { method: "POST", body: PartnerKeyCreateRequest.parse(body), schema: PartnerKeyCreateResponse });

export const postRotatePartnerKey = (keyId: string) =>
  apiFetch(REWARD_PATHS.partnerKeyAction(keyId, "rotate"), { method: "POST", body: {}, schema: PartnerKeyCreateResponse });

export const postRevokePartnerKey = (keyId: string) => apiFetch(REWARD_PATHS.partnerKeyAction(keyId, "revoke"), { method: "POST", body: {}, schema: PartnerKeyLoose });

// ---------------------------------------------------------------- staff: review queue and house campaigns

export const fetchCampaignReviewQueue = (cursor?: string | null) =>
  apiFetch(`${REWARD_PATHS.reviewCampaigns}${cursor ? `?cursor=${enc(cursor)}` : ""}`, { schema: CampaignReviewQueueResponse, cache: "no-cache" });

/** Moderator view of any campaign (exact task versions, quiz answers, budget). */
export const fetchReviewCampaign = (campaignId: string) => apiFetch(REWARD_PATHS.reviewCampaign(campaignId), { schema: CampaignLoose, cache: "no-cache" });

/** Audited decision (moderator / admin, never the campaign's owner). */
export const postCampaignDecision = (campaignId: string, body: z.input<typeof CampaignReviewDecisionRequest>) =>
  apiFetch<unknown>(REWARD_PATHS.reviewDecision(campaignId), { method: "POST", body: CampaignReviewDecisionRequest.parse(body) satisfies DecisionBody });

export const fetchHouseCampaigns = () => apiFetch(REWARD_PATHS.houseCampaigns, { schema: HouseListLoose, cache: "no-cache" });

export const postHouseCampaign = (body: z.input<typeof HouseCampaignCreateRequest>) =>
  apiFetch(REWARD_PATHS.houseCampaigns, { method: "POST", body: HouseCampaignCreateRequest.parse(body) satisfies HouseCampaignBody, schema: CampaignLoose });

/** Staff top-up of a house campaign: an audited allocation (not a card payment, not revenue). */
export const postHouseFunding = (campaignId: string, amountCents: number, idempotencyKey: string) =>
  apiFetch(REWARD_PATHS.houseFund(campaignId), {
    method: "POST",
    body: { amountCents },
    headers: { "idempotency-key": idempotencyKey },
    schema: CampaignLoose,
  });
